import React, { useEffect, useState } from "react";
import { Box, Grid, Typography, Chip, CircularProgress } from "@mui/material";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip } from "recharts";
import PageHeader from "../../components/PageHeader";
import SectionCard from "../../components/SectionCard";
import MiniStat from "../../components/MiniStat";
import { tokens } from "../../theme";
import { endPoints } from "../../constant/Environment";
import { getAllData } from "../../Utility/API";

const mono = '"IBM Plex Mono", monospace';

// "(direct)" stand-in for utm rows with a null/empty source or campaign.
const orDirect = (v) => (v && String(v).trim() ? v : "(direct)");
const pct = (part, whole) => (whole > 0 ? (part / whole) * 100 : 0);
const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

export default function UtmAttribution() {
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState({ totalVisitors: 0, totalSignups: 0 });
  const [campaigns, setCampaigns] = useState([]);
  const [revenue, setRevenue] = useState([]);
  const [signups, setSignups] = useState([]);
  const [mediums, setMediums] = useState([]);

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      try {
        const [sumRes, campRes, revRes, signRes, medRes] = await Promise.all([
          getAllData(endPoints.api.UTM_REPORT_SUMMARY),
          getAllData(endPoints.api.UTM_REPORT_CAMPAIGNS),
          getAllData(endPoints.api.UTM_REPORT_REVENUE),
          getAllData(endPoints.api.UTM_REPORT_SIGNUPS),
          getAllData(endPoints.api.UTM_REPORT_MEDIUM),
        ]);
        if (!active) return;
        if (sumRes?.status === "success") setSummary(sumRes.data || {});
        if (campRes?.status === "success") setCampaigns(campRes.data || []);
        if (revRes?.status === "success") setRevenue(revRes.data || []);
        if (signRes?.status === "success") setSignups(signRes.data || []);
        if (medRes?.status === "success") setMediums(medRes.data || []);
      } catch (e) {
        console.error("Failed to load UTM reports:", e);
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, []);

  const totalVisitors = Number(summary?.totalVisitors || 0);
  const totalSignups = Number(summary?.totalSignups || 0);
  const conversion = pct(totalSignups, totalVisitors);
  const totalRevenue = revenue.reduce((sum, r) => sum + Number(r?.revenue || 0), 0);

  // Signups-by-source rows for the chart (top sources by visitors).
  const signupChartData = signups
    .map((s) => ({
      source: orDirect(s.source),
      visitors: Number(s.totalVisitors || 0),
      signups: Number(s.totalUsers || 0),
    }))
    .sort((a, b) => b.visitors - a.visitors);

  const revenueChartData = revenue
    .map((r) => ({ source: orDirect(r.source), revenue: Number(r.revenue || 0) }))
    .filter((r) => r.revenue > 0)
    .sort((a, b) => b.revenue - a.revenue);

  const campaignRows = [...campaigns].sort(
    (a, b) => Number(b.totalVisitors || 0) - Number(a.totalVisitors || 0)
  );

  const mediumRows = [...mediums].sort(
    (a, b) => Number(b.totalVisitors || 0) - Number(a.totalVisitors || 0)
  );

  return (
    <Box>
      <PageHeader
        title="UTM Attribution"
        crumb="Monitor / UTM Attribution"
        subtitle="Campaign tracking · visitors → signups → revenue"
      />

      <Grid container spacing={1.75} sx={{ mb: 1.75 }}>
        <Grid item xs={6} md={3}>
          <MiniStat label="Total visitors" value={totalVisitors.toLocaleString()} color={tokens.primary} />
        </Grid>
        <Grid item xs={6} md={3}>
          <MiniStat label="Total signups" value={totalSignups.toLocaleString()} color={tokens.secondary} />
        </Grid>
        <Grid item xs={6} md={3}>
          <MiniStat label="Visitor → signup" value={`${conversion.toFixed(1)}%`} color={tokens.blue} />
        </Grid>
        <Grid item xs={6} md={3}>
          <MiniStat label="Attributed revenue" value={money(totalRevenue)} color={tokens.accent} />
        </Grid>
      </Grid>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <Grid container spacing={1.75}>
          <Grid item xs={12} lg={6}>
            <SectionCard title="Signups by source" subtitle="Visitors vs. converted signups">
              {signupChartData.length === 0 ? (
                <Typography variant="caption" color="text.secondary">No UTM traffic yet.</Typography>
              ) : (
                <Box sx={{ height: 260 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={signupChartData} margin={{ left: -18, right: 8, top: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={tokens.border} vertical={false} />
                      <XAxis dataKey="source" stroke={tokens.textDim} fontSize={11} tickLine={false} axisLine={false} />
                      <YAxis stroke={tokens.textDim} fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
                      <RTooltip cursor={{ fill: "rgba(255,255,255,0.04)" }} contentStyle={{ background: tokens.surface2, border: `1px solid ${tokens.border}`, borderRadius: 8 }} />
                      <Bar dataKey="visitors" name="Visitors" radius={[6, 6, 0, 0]} fill={tokens.blue} />
                      <Bar dataKey="signups" name="Signups" radius={[6, 6, 0, 0]} fill={tokens.secondary} />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              )}
            </SectionCard>
          </Grid>

          <Grid item xs={12} lg={6}>
            <SectionCard title="Revenue by source" subtitle="Completed purchases">
              {revenueChartData.length === 0 ? (
                <Typography variant="caption" color="text.secondary">No attributed revenue yet.</Typography>
              ) : (
                <Box sx={{ height: 260 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={revenueChartData} margin={{ left: -18, right: 8, top: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={tokens.border} vertical={false} />
                      <XAxis dataKey="source" stroke={tokens.textDim} fontSize={11} tickLine={false} axisLine={false} />
                      <YAxis stroke={tokens.textDim} fontSize={11} tickLine={false} axisLine={false} />
                      <RTooltip cursor={{ fill: "rgba(255,255,255,0.04)" }} formatter={(v) => money(v)} contentStyle={{ background: tokens.surface2, border: `1px solid ${tokens.border}`, borderRadius: 8 }} />
                      <Bar dataKey="revenue" name="Revenue" radius={[6, 6, 0, 0]} fill={tokens.accent} />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              )}
            </SectionCard>
          </Grid>

          <Grid item xs={12}>
            <SectionCard title="By medium" subtitle="How the traffic arrived — cpc, email, social, referral">
              <Box sx={{ overflowX: "auto" }}>
                <Box
                  component="table"
                  sx={{
                    width: "100%",
                    borderCollapse: "collapse",
                    "& th": { textAlign: "left", color: tokens.textDim, fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 700, p: "10px 12px", borderBottom: `1px solid ${tokens.border}`, whiteSpace: "nowrap" },
                    "& td": { p: "12px", borderBottom: `1px solid ${tokens.border}`, fontSize: 13, fontFamily: mono },
                  }}
                >
                  <thead>
                    <tr>
                      <th>Medium</th>
                      <th>Visitors</th>
                      <th>Signups</th>
                      <th>Conv.</th>
                      <th>Revenue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mediumRows.length === 0 ? (
                      <tr>
                        <td colSpan={5} style={{ textAlign: "center", color: tokens.textDim }}>No medium data yet.</td>
                      </tr>
                    ) : (
                      mediumRows.map((r, i) => {
                        const visitors = Number(r.totalVisitors || 0);
                        const users = Number(r.totalUsers || 0);
                        return (
                          <tr key={`${r.medium}-${i}`}>
                            <td style={{ fontFamily: "inherit" }}>
                              <Chip size="small" label={orDirect(r.medium)} sx={{ bgcolor: tokens.surface2, color: tokens.text, fontWeight: 600 }} />
                            </td>
                            <td>{visitors.toLocaleString()}</td>
                            <td>{users.toLocaleString()}</td>
                            <td style={{ color: tokens.secondary }}>{pct(users, visitors).toFixed(1)}%</td>
                            <td>{money(r.revenue)}</td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </Box>
              </Box>
            </SectionCard>
          </Grid>

          <Grid item xs={12}>
            <SectionCard title="Campaign attribution" subtitle="Visitors, signups, conversion & revenue by campaign">
              <Box sx={{ overflowX: "auto" }}>
                <Box
                  component="table"
                  sx={{
                    width: "100%",
                    borderCollapse: "collapse",
                    "& th": { textAlign: "left", color: tokens.textDim, fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 700, p: "10px 12px", borderBottom: `1px solid ${tokens.border}`, whiteSpace: "nowrap" },
                    "& td": { p: "12px", borderBottom: `1px solid ${tokens.border}`, fontSize: 13, fontFamily: mono },
                  }}
                >
                  <thead>
                    <tr>
                      <th>Campaign</th>
                      <th>Source</th>
                      <th>Visitors</th>
                      <th>Signups</th>
                      <th>Conv.</th>
                      <th>Revenue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {campaignRows.length === 0 ? (
                      <tr>
                        <td colSpan={6} style={{ textAlign: "center", color: tokens.textDim }}>No campaigns tracked yet.</td>
                      </tr>
                    ) : (
                      campaignRows.map((r, i) => {
                        const visitors = Number(r.totalVisitors || 0);
                        const users = Number(r.totalUsers || 0);
                        return (
                          <tr key={`${r.campaign}-${r.source}-${i}`}>
                            <td style={{ fontFamily: "inherit" }}>
                              <Chip size="small" label={orDirect(r.campaign)} sx={{ bgcolor: tokens.surface2, color: tokens.text, fontWeight: 600 }} />
                            </td>
                            <td>{orDirect(r.source)}</td>
                            <td>{visitors.toLocaleString()}</td>
                            <td>{users.toLocaleString()}</td>
                            <td style={{ color: tokens.secondary }}>{pct(users, visitors).toFixed(1)}%</td>
                            <td>{money(r.revenue)}</td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </Box>
              </Box>
            </SectionCard>
          </Grid>
        </Grid>
      )}
    </Box>
  );
}
